/* components/versioning/VersionsBlock.jsx — встраиваемый блок «История версий (N)».
   Единый кросс-каттинг слой для IngredientEditor / TTKEditor / MenuEditor.
   • список снимков (номер/дата/summary/автор), активная версия выделена;
   • «Просмотреть» → &v=<n> в URL → SnapshotView (+ хвостовая крошка «Версия N»);
   • «Сравнить» → VersionCompareModal (DiffView); «Восстановить» → ConfirmDialog → toast+баннер;
   • «Поделиться ссылкой» — копирует URL с &v=<n> (shareable, фикс боли №7).
   props: kind∈{card,ttk,menu}, id, currentSnapshot.
   ⚠️ ДАННЫЕ: блок САМ тянет lib/api listVersions/restoreVersion (исключение из «данные только
   пропсами»). kind 1:1 с фасадом (ttk/card/menu); listVersions гидрирует snapshot каждой версии.
   «Создать снимок» вручную НЕТ — версии авто-фиксируются Django-сигналами при сохранении сущности
   (см. реестр дыр: createSnapshot-эндпоинта не существует). ХЕНДОФФ → frontend/src/components/versioning/. */

import React from 'react';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { QueryBoundary } from '../patterns/States.jsx';
import { Button } from '../ui/Button.jsx';
import { IconButton } from '../ui/Buttons.jsx';
import { Icon } from '../ui/Icon.jsx';
import { SnapshotView } from './SnapshotView.jsx';
import { VersionCompareModal } from './VersionCompareModal.jsx';
import { hooks } from '../../hooks/index.jsx';
import { listVersions, restoreVersion } from '../../lib/api/index.js';
import { fmt } from '../../lib/format.js';
import { RR } from '../../lib/router.jsx';

  const { useState } = React;
  const { useFakeQuery, parseStack } = hooks;
  const { useSearchParams } = RR;

  // &v скоупится к ВЕРХНЕМУ сегменту ?edit (IA §4.1: «&v=<n> — версия верхнего»).
  // Блок чужого уровня НЕ должен подхватывать v верхней сущности.
  function isTopOfStack(editParam, kind, id) {
    const stack = parseStack(editParam);
    const top = stack.length ? stack[stack.length - 1] : null;
    return !!top && top.type === kind && String(top.id) === String(id);
  }

  // HISTORY-роутинг: маршрут живёт в реальном пути (…/kitchen?edit=…&v=…).
  // Доклеиваем &v=N к текущему query, сохраняя путь и остальные параметры.
  function buildShareUrl(n) {
    const loc = window.location;
    const sp = new URLSearchParams(loc.search || '');
    sp.set('v', String(n));
    const qs = sp.toString();
    return loc.origin + loc.pathname + (qs ? '?' + qs : '');
  }

  function VersionsBlock({ kind, id, currentSnapshot }) {
    const [params, setParams] = useSearchParams();
    const toast = useToast();
    const confirm = useConfirm();
    // версия читается ТОЛЬКО когда этот блок — верхний сегмент ?edit (deep-link не разъезжается на вложенных уровнях)
    const isTop = isTopOfStack(params.get('edit'), kind, id);
    const vParam = isTop ? params.get('v') : null;
    const [compare, setCompare] = useState(null); // baseVersion
    const [restoring, setRestoring] = useState(false);
    const [restoredFrom, setRestoredFrom] = useState(null);

    const query = useFakeQuery('versions-' + kind + '-' + id, () => listVersions(kind, id), [kind, id]);
    const versions = query.data || [];
    const activeNum = versions.length ? Math.max.apply(null, versions.map((v) => v.version_number)) : null;

    const setV = (n) => setParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (n == null) sp.delete('v'); else sp.set('v', String(n));
      return sp;
    });

    const doRestore = async (n) => {
      const ok = await confirm({ title: 'Восстановить версию ' + n + '?', body: 'Это перезапишет текущее состояние данными версии ' + n + '. Текущее состояние будет сохранено как новая версия.', confirmLabel: 'Восстановить', tone: 'warn' });
      if (!ok) return;
      setRestoring(true);
      try {
        await restoreVersion(kind, id, n);
        setRestoredFrom(n);
        setCompare(null);
        setV(null);
        toast.show('Восстановлено из версии ' + n, 'success');
        query.reload();
      } catch (e) { toast.show('Не удалось восстановить', 'danger'); }
      setRestoring(false);
    };

    const share = async (n) => {
      const url = buildShareUrl(n);
      try { await navigator.clipboard.writeText(url); toast.show('Ссылка на версию ' + n + ' скопирована', 'success'); }
      catch (e) { toast.show('Ссылка: ' + url, 'muted', 4000); }
    };

    // ── снимок версии открыт по &v=n ──
    if (vParam) {
      const version = versions.find((v) => String(v.version_number) === String(vParam));
      if (query.loading) return <div className="versions versions--loading"><div className="skeleton versions__skel" /></div>;
      if (!version) {
        return <div className="versions"><p className="text-mute">Версия {vParam} не найдена.</p><Button variant="ghost" size="sm" iconLeft="arrow-left" onClick={() => setV(null)}>К истории</Button></div>;
      }
      return <SnapshotView kind={kind} version={version} busy={restoring}
        onReturn={() => setV(null)} onRestore={() => doRestore(version.version_number)} />;
    }

    // ── загрузка / ошибка / пусто ──
    if (query.loading) {
      return <div className="versions versions--loading">{[0, 1, 2].map((i) => <div key={i} className="skeleton versions__skel" />)}</div>;
    }
    if (query.error) return <QueryBoundary error={query.error} onRetry={query.reload}>{null}</QueryBoundary>;
    if (!versions.length) {
      return (
        <div className="versions versions--empty">
          <Icon name="clock" size={32} />
          <p className="t-body text-dim">У этой сущности пока нет сохранённых версий. Версия фиксируется при каждом сохранении.</p>
        </div>
      );
    }

    return (
      <div className="versions">
        {restoredFrom != null && (
          <div className="callout callout--ok versions__banner">
            <Icon name="check-circle" size={18} />
            <span className="grow">Восстановлено из версии {restoredFrom}. Проверьте данные и сохраните.</span>
            <button type="button" className="callout__close" onClick={() => setRestoredFrom(null)} aria-label="Скрыть"><Icon name="close" size={15} /></button>
          </div>
        )}
        <h4 className="versions__title t-caption">История версий ({versions.length})</h4>
        <ul className="versions__list">
          {versions.map((v) => {
            const active = v.version_number === activeNum;
            return (
              <li key={v.version_number} className={'version-card' + (active ? ' is-active' : '')}>
                <div className="version-card__main">
                  <span className="version-card__num num">v{v.version_number}</span>
                  <div className="version-card__info">
                    <div className="version-card__summary">{v.summary}{active && <span className="badge badge--accent badge--sm">текущая</span>}</div>
                    <div className="version-card__meta t-caption text-dim">{fmt.date(v.created_at)} · {v.author_email}</div>
                  </div>
                  <IconButton icon="link" size="sm" variant="ghost" tooltip="Поделиться ссылкой" onClick={() => share(v.version_number)} />
                </div>
                <div className="version-card__act">
                  <Button variant="ghost" size="sm" iconLeft="eye" onClick={() => setV(v.version_number)}>Просмотреть</Button>
                  <Button variant="ghost" size="sm" iconLeft="copy" onClick={() => setCompare(v)}>Сравнить</Button>
                  {!active && <Button variant="ghost" size="sm" iconLeft="refresh" onClick={() => doRestore(v.version_number)}>Восстановить</Button>}
                </div>
              </li>
            );
          })}
        </ul>

        {compare && (
          <VersionCompareModal open kind={kind} baseVersion={compare} versions={versions}
            currentSnapshot={currentSnapshot} restoring={restoring}
            onClose={() => setCompare(null)} onRestore={doRestore} />
        )}
      </div>
    );
  }

export { VersionsBlock };
